/**
 * Audio utility functions for playing various UI sounds.
 * The implementation now removes stray XML tags that caused a syntax error
 * and adds a tiny amount of defensive error handling while keeping the
 * original behaviour unchanged.
 */

const getAudioContext = (() => {
  // Create a single AudioContext instance and reuse it.
  // This avoids the "AudioContext already created" warning in some browsers.
  let ctx: AudioContext | null = null;
  return (): AudioContext => {
    if (!ctx) {
      ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return ctx;
  };
})();

/**
 * Play a single tone.
 *
 * @param frequency Frequency of the tone in hertz.
 * @param duration  Length of the tone in seconds.
 * @param type      Oscillator waveform type.
 * @param volume    Gain value (0‑1). Defaults to 0.15.
 */
function playTone(
  frequency: number,
  duration: number,
  type: 'sine' | 'square' | 'sawtooth' | 'triangle',
  volume = 0.15,
): void {
  try {
    const ctx = getAudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    // Fade out to avoid clicks.
    gain.gain.exponentialRampToValueAtTime(
      0.001,
      ctx.currentTime + duration,
    );

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + duration);
  } catch (err) {
    // Silently fail, but keep the error for debugging in non‑production builds.
    if (import.meta.env.DEV) {
      console.error('playTone error:', err);
    }
  }
}

/**
 * Play a short "correct" jingle.
 */
export function playCorrectSound(): void {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5];

    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + i * 0.12);
      gain.gain.setValueAtTime(0.15, now + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(
        0.001,
        now + i * 0.12 + 0.3,
      );

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.3);
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error('playCorrectSound error:', err);
    }
  }
}

/**
 * Play a short "wrong" sound sequence.
 */
export function playWrongSound(): void {
  playTone(200, 0.15, 'square', 0.1);
  setTimeout(() => playTone(150, 0.3, 'square', 0.1), 150);
}

/**
 * Play a celebratory "win" melody.
 */
export function playWinSound(): void {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const melody = [
      523.25,
      587.33,
      659.25,
      783.99,
      659.25,
      783.99,
      1046.5,
    ];

    melody.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + i * 0.15);
      gain.gain.setValueAtTime(0.15, now + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(
        0.001,
        now + i * 0.15 + 0.35,
      );

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now + i * 0.15);
      osc.stop(now + i * 0.15 + 0.35);
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error('playWinSound error:', err);
    }
  }
}

/**
 * Play a "lose" descending tone pattern.
 */
export function playLoseSound(): void {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const notes = [392, 349.23, 311.13, 261.63];

    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now + i * 0.25);
      gain.gain.setValueAtTime(0.12, now + i * 0.25);
      gain.gain.exponentialRampToValueAtTime(
        0.001,
        now + i * 0.25 + 0.4,
      );

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now + i * 0.25);
      osc.stop(now + i * 0.25 + 0.4);
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error('playLoseSound error:', err);
    }
  }
}

/**
 * Play a light "key click" sound.
 */
export function playKeyClickSound(): void {
  playTone(800, 0.05, 'sine', 0.05);
}
